package com.smashvoter

import org.jetbrains.compose.web.css.*

/**
 * Design tokens derived from the AppTheme example (dark mode):
 *   background  → AppColorPalette.Solid.black  (#0a0a0a)
 *   surface     → #1C1C1E  (from PodiumCard / CourtsListScreen)
 *   text        → AppColorPalette.Solid.white
 *   secondary   → #999999  (from CourtsListScreen secondary labels)
 *   accent      → #E10D05  smash red — the exact fillColor from smash.xml
 *   error       → #E94F37  arcade red (from SplitFlapCountdown)
 *
 * Typography uses Rubik (loaded via Google Fonts in index.html).
 */
object SmashColors {
    val background     = "#19191B"
    val surface        = "#1C1C1E"
    val surfaceRaised  = "#2C2C2E"
    val border         = "#3C3C3E"
    val textPrimary    = "#FFFFFF"
    val textSecondary  = "#999999"
    val accent         = "#E10D05"   // smash red — exact fillColor from smash.xml; used for text/highlights
    val accentButton   = "#B91C1C"   // muted variant for button fills — same hue, less vibrant
    val accentDark     = "#FFFFFF"   // white text on red backgrounds
    val error          = "#E94F37"   // arcade red (warmer variant, for error states)
}

// ---------------------------------------------------------------------------
// Reusable StyleScope helpers
// ---------------------------------------------------------------------------

/** Full-width primary button — muted red fill with white text. */
fun StyleScope.smashPrimaryButton() {
    width(100.percent)
    padding(12.px)
    backgroundColor(Color(SmashColors.accentButton))
    color(Color(SmashColors.accentDark))
    border(0.px, LineStyle.None, Color.transparent)
    borderRadius(8.px)
    fontSize(16.px)
    fontWeight("700")
    letterSpacing(0.3.px)
    property("cursor", "pointer")
    property("transition", "opacity 0.15s, transform 0.1s")
}

/** Inline secondary/ghost button — transparent with muted red border. */
fun StyleScope.smashGhostButton() {
    padding(10.px, 20.px)
    backgroundColor(Color.transparent)
    color(Color(SmashColors.accentButton))
    border(1.px, LineStyle.Solid, Color(SmashColors.accentButton))
    borderRadius(8.px)
    fontSize(14.px)
    fontWeight("600")
    property("cursor", "pointer")
    property("transition", "opacity 0.15s")
}

/** Small nav-bar button — subtle white-on-transparent. */
fun StyleScope.smashNavButton() {
    padding(6.px, 14.px)
    backgroundColor(Color("rgba(255,255,255,0.1)"))
    color(Color(SmashColors.textPrimary))
    border(1.px, LineStyle.Solid, Color("rgba(255,255,255,0.2)"))
    borderRadius(6.px)
    fontSize(14.px)
    fontWeight("500")
    property("cursor", "pointer")
    property("transition", "background-color 0.15s")
}

/** Dark text input field. */
fun StyleScope.smashInput() {
    width(100.percent)
    padding(10.px, 12.px)
    marginTop(6.px)
    marginBottom(16.px)
    backgroundColor(Color(SmashColors.surfaceRaised))
    color(Color(SmashColors.textPrimary))
    border(1.px, LineStyle.Solid, Color(SmashColors.border))
    borderRadius(8.px)
    fontSize(16.px)
    property("outline", "none")
    property("box-sizing", "border-box")
}


